import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, TrendingUp, Shield, Globe, Target, Lightbulb,
  CheckCircle2, Send, Building2, Users, DollarSign, Zap,
  FileText, Cpu, FlaskConical, Rocket, Star, Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1, ease: 'easeOut' } }),
};

/* ── Sections Data ──────────────────────────────────────────────────────── */

const problems = [
  'Millions lose motor function to stroke/injury with limited rehab options',
  'Cancer treatments cause systemic damage — precision delivery is rare',
  'Medical devices remain inaccessible in emerging markets like India',
  'Diagnostic delays cost lives — reactive medicine dominates over preventive',
];

const solutions = [
  { product: 'BioSleeve', desc: 'Wearable neural-muscular interface restoring motor control through real-time EMG + AI', status: 'prototype' },
  { product: 'NanoMed System', desc: 'Edible nanocarriers delivering targeted therapeutics to precise cellular locations', status: 'research' },
];

const traction = [
  { icon: FileText, value: '5', label: 'Published Articles', sub: 'Research & medical insights on Medium' },
  { icon: Cpu, value: '2', label: 'Products in Dev', sub: 'BioSleeve & NanoMed Delivery System' },
  { icon: FlaskConical, value: '1', label: 'Patent Application', sub: 'Provisional filing for BioSleeve interface' },
  { icon: Users, value: '2025', label: 'Founded', sub: 'Pre-seed stage, building the core team' },
  { icon: Globe, value: 'India', label: 'Primary Market', sub: 'With global expansion roadmap' },
  { icon: Rocket, value: 'Pre-Seed', label: 'Raising Now', sub: 'Open to investors & strategic partners' },
];

const bizModels = [
  { icon: Building2, title: 'B2B Licensing', desc: 'License BioSleeve & NanoMed IP to medical device manufacturers and healthcare providers globally.', color: 'text-bvx-cyan', bg: 'bg-bvx-cyan/10 border-bvx-cyan/20' },
  { icon: Shield, title: 'Healthcare Partnerships', desc: 'Partner with national health systems (AIIMS, NHS) for large-scale deployment of affordable rehabilitation solutions.', color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20' },
  { icon: DollarSign, title: 'Direct Product Sales', desc: 'Consumer and clinical-grade BioSleeve sold through medical device channels and direct-to-patient.', color: 'text-bvx-green', bg: 'bg-bvx-green/10 border-bvx-green/20' },
  { icon: Zap, title: 'SaaS Platform', desc: 'Monthly subscription for BioSleeve companion app — real-time analytics, rehabilitation programs, physician dashboard.', color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/20' },
];

const roadmapItems = [
  { q: 'Q1 2025', title: 'Company Founded', desc: 'Biovex Technologies established. Core research begins on BioSleeve concept.', done: true },
  { q: 'Q3 2025', title: 'BioSleeve Prototype v1', desc: 'First working prototype of BioSleeve EMG sensor array and edge AI processing.', done: true },
  { q: 'Q1 2026', title: 'Pre-Seed Fundraising', desc: 'Raising pre-seed round to fund clinical prototype development and NanoMed research.', active: true },
  { q: 'Q3 2026', title: 'Clinical Trials Begin', desc: 'BioSleeve pilot trials with rehabilitation centers across India.', done: false },
  { q: 'Q1 2027', title: 'Regulatory Approval', desc: 'CE Mark and India CDSCO regulatory approval for BioSleeve medical device.', done: false },
  { q: 'Q3 2027', title: 'NanoMed Phase I', desc: 'Begin Phase I human safety trials for NanoMed Delivery System.', done: false },
  { q: 'Q1 2028', title: 'Series A Round', desc: 'Scale operations, expand team, and launch product commercially across India & UK.', done: false },
];

const whyInvest = [];

/* ── Timeline Graph Component ────────────────────────────────────────────── */

const TimelineGraph = ({ items }) => (
  <div className="glass-card p-8 overflow-x-auto" data-testid="roadmap-timeline">
    <div className="min-w-[680px]">
      {/* Legend */}
      <div className="flex items-center gap-6 mb-10">
        {[
          { color: 'bg-bvx-green', border: 'border-bvx-green', label: 'Completed' },
          { color: 'bg-bvx-cyan', border: 'border-bvx-cyan', label: 'In Progress' },
          { color: 'bg-bvx-bg', border: 'border-bvx-border', label: 'Planned' },
        ].map((l) => (
          <div key={l.label} className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${l.color} border ${l.border}`} />
            <span className="text-white/45 text-xs font-inter-tight">{l.label}</span>
          </div>
        ))}
      </div>

      {/* Top labels (even index) */}
      <div className="flex">
        {items.map((item, i) => (
          <div key={i} style={{ width: `${100 / items.length}%` }} className="px-1 text-center">
            {i % 2 === 0 ? (
              <div className="flex flex-col items-center gap-1 pb-2">
                <p className="font-inter-tight text-white/65 text-xs leading-tight">{item.title}</p>
                <span className={`font-rajdhani font-semibold uppercase tracking-wider text-xs ${item.done ? 'text-bvx-green' : item.active ? 'text-bvx-cyan' : 'text-white/30'}`}>{item.q}</span>
                <div className="w-px h-5 bg-bvx-border" />
              </div>
            ) : <div className="h-[72px]" />}
          </div>
        ))}
      </div>

      {/* Track + dots */}
      <div className="relative flex items-center h-5">
        <div className="absolute inset-x-0 top-1/2 h-0.5 bg-bvx-border -translate-y-1/2 rounded-full" />
        <motion.div
          className="absolute left-0 top-1/2 h-0.5 -translate-y-1/2 rounded-full"
          style={{ background: 'linear-gradient(90deg, #39FF14, #00F0FF)' }}
          initial={{ width: 0 }}
          whileInView={{ width: `${(100 / items.length) * 2.5}%` }}
          viewport={{ once: true }}
          transition={{ duration: 1.5, ease: 'easeOut', delay: 0.3 }}
        />
        {items.map((item, i) => (
          <div key={i} style={{ width: `${100 / items.length}%` }} className="flex justify-center">
            <div className={`w-4 h-4 rounded-full border-2 relative z-10 shrink-0 ${item.done ? 'bg-bvx-green border-bvx-green' : item.active ? 'bg-bvx-cyan border-bvx-cyan' : 'bg-bvx-bg border-bvx-border'}`}>
              {item.active && <div className="absolute inset-0 rounded-full bg-bvx-cyan/50 animate-ping" />}
            </div>
          </div>
        ))}
      </div>

      {/* Bottom labels (odd index) */}
      <div className="flex">
        {items.map((item, i) => (
          <div key={i} style={{ width: `${100 / items.length}%` }} className="px-1 text-center">
            {i % 2 !== 0 ? (
              <div className="flex flex-col items-center gap-1 pt-2">
                <div className="w-px h-5 bg-bvx-border" />
                <span className={`font-rajdhani font-semibold uppercase tracking-wider text-xs ${item.done ? 'text-bvx-green' : item.active ? 'text-bvx-cyan' : 'text-white/30'}`}>{item.q}</span>
                <p className="font-inter-tight text-white/65 text-xs leading-tight">{item.title}</p>
              </div>
            ) : <div className="h-[72px]" />}
          </div>
        ))}
      </div>

      {/* Active item callout */}
      {items.filter((it) => it.active).map((item) => (
        <div key={item.q} className="mt-8 border-t border-bvx-border pt-5 flex items-start gap-3">
          <div className="w-2 h-2 rounded-full bg-bvx-cyan shrink-0 mt-1.5 animate-pulse-glow" />
          <div>
            <span className="text-bvx-cyan text-xs font-rajdhani font-semibold uppercase tracking-widest">{item.q} · Currently Active</span>
            <p className="text-white/60 text-sm font-inter-tight mt-1">{item.title} — {item.desc}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

/* ── Main Component ─────────────────────────────────────────────────────── */

export default function InvestorsPage() {
  const [form, setForm] = useState({ name: '', email: '', organization: '', investment_range: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.title = 'For Investors | Biovex Technologies';
    return () => { document.title = 'Biovex Technologies'; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error('Name and email are required.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/pitch-request`, form);
      setSubmitted(true);
      toast.success('Request received! We\'ll send the pitch deck within 24 hours.');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="pt-24">

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden px-6 md:px-12 lg:px-24 py-20 md:py-32">
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 80% 60% at 15% 50%, rgba(0,240,255,0.1) 0%, transparent 65%)' }} />
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(57,255,20,0.07) 0%, transparent 70%)' }} />
        <div className="max-w-7xl relative z-10">
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
            <span className="section-label">For Investors</span>
          </motion.div>
          <motion.h1
            variants={fadeUp} initial="hidden" animate="visible" custom={1}
            className="font-outfit font-bold text-5xl sm:text-6xl lg:text-7xl text-white mt-6 mb-6 leading-tight"
            data-testid="investors-heading"
          >
            Investing in the<br />
            Future of{' '}
            <span className="text-gradient-cyan">Biotech Innovation</span>
          </motion.h1>
          <motion.p
            variants={fadeUp} initial="hidden" animate="visible" custom={2}
            className="text-white/55 font-inter-tight text-lg max-w-2xl leading-relaxed mb-10"
          >
            Biovex Technologies is building the next generation of human augmentation — from neural-muscular interfaces to molecular nanotechnology. We're raising our pre-seed round and inviting aligned investors to be part of this journey.
          </motion.p>
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={3} className="flex flex-wrap gap-4">
            <a href="#pitch-deck" className="btn-primary flex items-center gap-2" data-testid="hero-pitch-deck-btn">
              <FileText size={15} /> Request Pitch Deck
            </a>
            <Link to="/contact" className="btn-outline flex items-center gap-2" data-testid="hero-contact-founder-btn">
              Contact Founder <ArrowRight size={15} />
            </Link>
          </motion.div>

          {/* Key Numbers Strip */}
          <motion.div
            variants={fadeUp} initial="hidden" animate="visible" custom={4}
            className="grid grid-cols-2 sm:grid-cols-4 gap-5 mt-14 pt-10 border-t border-bvx-border"
          >
            {[
              { v: 'Pre-Seed', l: 'Round Stage' },
              { v: '$800B+', l: 'Market Opportunity' },
              { v: '2', l: 'Products in Pipeline' },
              { v: 'Open', l: 'For Investment' },
            ].map((s, i) => (
              <div key={i} data-testid={`hero-stat-${i}`}>
                <p className="font-outfit font-bold text-3xl text-bvx-cyan">{s.v}</p>
                <p className="text-white/45 text-sm font-inter-tight mt-1">{s.l}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Vision & Opportunity ──────────────────────────────────────────── */}
      <section className="px-6 md:px-12 lg:px-24 py-24 border-t border-bvx-border">
        <div className="max-w-7xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-12">
            <span className="section-label">Vision & Opportunity</span>
            <h2 className="font-outfit font-bold text-4xl sm:text-5xl text-white mt-6 leading-tight">
              Why Biovex,<br />Why <span className="text-gradient-cyan">Now</span>?
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="glass-card p-8 card-hover" data-testid="vision-opp-card-1">
              <div className="w-10 h-10 rounded-lg bg-bvx-cyan/10 border border-bvx-cyan/20 flex items-center justify-center mb-5">
                <Target size={18} className="text-bvx-cyan" />
              </div>
              <h3 className="font-outfit font-bold text-white text-xl mb-3">What We're Building</h3>
              <p className="text-white/55 font-inter-tight text-sm leading-relaxed">
                Biovex Technologies develops two core platforms: <strong className="text-white">BioSleeve</strong> — a wearable neural-muscular interface for rehabilitation and human augmentation — and the <strong className="text-white">NanoMed Delivery System</strong>, an edible nanocarrier platform for targeted drug delivery at the cellular level.
              </p>
              <p className="text-white/55 font-inter-tight text-sm leading-relaxed mt-3">
                Both platforms are built on proprietary AI and materials science, positioned at the intersection of the fastest-growing segments in global healthcare technology.
              </p>
            </motion.div>
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" custom={0.2} viewport={{ once: true }} className="glass-card p-8 card-hover" data-testid="vision-opp-card-2">
              <div className="w-10 h-10 rounded-lg bg-bvx-green/10 border border-bvx-green/20 flex items-center justify-center mb-5">
                <TrendingUp size={18} className="text-bvx-green" />
              </div>
              <h3 className="font-outfit font-bold text-white text-xl mb-3">Why This Moment</h3>
              <p className="text-white/55 font-inter-tight text-sm leading-relaxed">
                India is undergoing a historic transformation in healthcare infrastructure. With 1.4B people, a rapidly aging population, and increasing government investment in medtech, the timing for a homegrown biotech innovator has never been stronger.
              </p>
              <p className="text-white/55 font-inter-tight text-sm leading-relaxed mt-3">
                Simultaneously, AI has crossed a threshold — making sub-10ms neural signal processing viable on edge devices. The convergence of biology and AI is here. Biovex is positioned at its centre.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Problem & Solution ────────────────────────────────────────────── */}
      <section className="px-6 md:px-12 lg:px-24 py-24 border-t border-bvx-border">
        <div className="max-w-7xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-12">
            <span className="section-label">Problem & Solution</span>
            <h2 className="font-outfit font-bold text-4xl sm:text-5xl text-white mt-6 leading-tight">
              Real Problems,<br /><span className="text-gradient-green">Engineered Solutions</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Problems */}
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="glass-card p-8" data-testid="problems-card">
              <h3 className="font-rajdhani font-semibold uppercase tracking-widest text-red-400 text-xs mb-5">The Challenges</h3>
              <div className="space-y-4">
                {problems.map((p, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0 mt-2" />
                    <p className="text-white/60 font-inter-tight text-sm leading-relaxed">{p}</p>
                  </div>
                ))}
              </div>
            </motion.div>
            {/* Solutions */}
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" custom={0.2} viewport={{ once: true }} className="space-y-5" data-testid="solutions-card">
              <h3 className="font-rajdhani font-semibold uppercase tracking-widest text-bvx-green text-xs mb-5">Biovex Solutions</h3>
              {solutions.map((s, i) => (
                <div key={i} className="glass-card p-6 card-hover">
                  <div className="flex items-center gap-3 mb-3">
                    <CheckCircle2 size={16} className="text-bvx-green shrink-0" />
                    <span className="font-outfit font-bold text-white text-base">{s.product}</span>
                    <span className={`status-badge status-${s.status} ml-auto`}>{s.status}</span>
                  </div>
                  <p className="text-white/55 font-inter-tight text-sm leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Traction ──────────────────────────────────────────────────────── */}
      <section className="px-6 md:px-12 lg:px-24 py-24 border-t border-bvx-border">
        <div className="max-w-7xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-12">
            <span className="section-label">Traction</span>
            <h2 className="font-outfit font-bold text-4xl sm:text-5xl text-white mt-6 leading-tight">
              Progress to Date
            </h2>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
            {traction.map(({ icon: Icon, value, label, sub }, i) => (
              <motion.div
                key={label}
                variants={fadeUp} initial="hidden" whileInView="visible" custom={i * 0.08} viewport={{ once: true }}
                className="glass-card p-6 card-hover"
                data-testid={`traction-${i}`}
              >
                <Icon size={18} className="text-bvx-cyan mb-4" />
                <p className="font-outfit font-bold text-3xl text-white mb-1">{value}</p>
                <p className="font-inter-tight font-medium text-white/80 text-sm">{label}</p>
                <p className="text-white/40 text-xs font-inter-tight mt-1 leading-relaxed">{sub}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Business Model ────────────────────────────────────────────────── */}
      <section className="px-6 md:px-12 lg:px-24 py-24 border-t border-bvx-border">
        <div className="max-w-7xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-12">
            <span className="section-label">Business Model</span>
            <h2 className="font-outfit font-bold text-4xl sm:text-5xl text-white mt-6 leading-tight">
              How We Generate <span className="text-gradient-cyan">Revenue</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {bizModels.map(({ icon: Icon, title, desc, color, bg }, i) => (
              <motion.div
                key={title}
                variants={fadeUp} initial="hidden" whileInView="visible" custom={i * 0.1} viewport={{ once: true }}
                className="glass-card p-7 card-hover"
                data-testid={`biz-model-${i}`}
              >
                <div className={`w-10 h-10 rounded-lg border flex items-center justify-center mb-5 ${bg}`}>
                  <Icon size={18} className={color} />
                </div>
                <h3 className="font-outfit font-bold text-white text-lg mb-3">{title}</h3>
                <p className="text-white/50 font-inter-tight text-sm leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Roadmap ───────────────────────────────────────────────────────── */}
      <section className="px-6 md:px-12 lg:px-24 py-24 border-t border-bvx-border">
        <div className="max-w-7xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-10">
            <span className="section-label">Roadmap</span>
            <h2 className="font-outfit font-bold text-4xl sm:text-5xl text-white mt-6 leading-tight">
              Key Milestones
            </h2>
          </motion.div>
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <TimelineGraph items={roadmapItems} />
          </motion.div>
        </div>
      </section>

      {/* ── Pitch Deck CTA ────────────────────────────────────────────────── */}
      <section id="pitch-deck" className="px-6 md:px-12 lg:px-24 py-24 border-t border-bvx-border">
        <div className="max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left — copy */}
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              <span className="section-label">Raise Round</span>
              <h2 className="font-outfit font-bold text-4xl sm:text-5xl text-white mt-6 mb-5 leading-tight">
                We're Raising<br />
                Our <span className="text-gradient-cyan">Pre-Seed Round</span>
              </h2>
              <p className="text-white/55 font-inter-tight text-sm leading-relaxed mb-5">
                Biovex Technologies is actively seeking pre-seed investment to fund:
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'BioSleeve clinical-grade prototype development',
                  'NanoMed nanocarrier research & formulation',
                  'Core team expansion (engineers, biologists)',
                  'Regulatory pathway preparation (CDSCO, CE Mark)',
                  'IP filing and patent portfolio building',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-white/60 text-sm font-inter-tight">
                    <CheckCircle2 size={14} className="text-bvx-green shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="glass-card p-5 flex items-start gap-4">
                <Lock size={16} className="text-bvx-cyan shrink-0 mt-0.5" />
                <p className="text-white/50 font-inter-tight text-xs leading-relaxed">
                  Detailed financials, valuation, and use of funds are shared exclusively with verified investors after pitch deck request. This page contains indicative information only.
                </p>
              </div>
              <div className="mt-6">
                <Link to="/contact" className="inline-flex items-center gap-2 text-bvx-cyan text-sm font-medium hover:gap-3 transition-all duration-200" data-testid="contact-founder-link">
                  Prefer to email directly? Contact Founder <ArrowRight size={14} />
                </Link>
              </div>
            </motion.div>

            {/* Right — form */}
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" custom={0.2} viewport={{ once: true }}>
              {submitted ? (
                <div className="glass-card p-10 flex flex-col items-start gap-4" data-testid="pitch-success">
                  <div className="w-14 h-14 rounded-2xl bg-bvx-cyan/15 border border-bvx-cyan/30 flex items-center justify-center">
                    <CheckCircle2 size={26} className="text-bvx-cyan" />
                  </div>
                  <h3 className="font-outfit font-bold text-white text-xl">Request Received</h3>
                  <p className="text-white/55 font-inter-tight text-sm leading-relaxed">
                    Thank you for your interest in Biovex Technologies. Our founder will review your request and send the pitch deck within 24 hours.
                  </p>
                  <button
                    onClick={() => { setSubmitted(false); setForm({ name: '', email: '', organization: '', investment_range: '', message: '' }); }}
                    className="btn-outline text-sm mt-2"
                    data-testid="another-request-btn"
                  >
                    Submit Another Request
                  </button>
                </div>
              ) : (
                <div className="glass-card p-8">
                  <h3 className="font-outfit font-bold text-white text-lg mb-1">Request Pitch Deck</h3>
                  <p className="text-white/40 font-inter-tight text-xs mb-6">Shared securely after review. No spam, ever.</p>
                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="pitch-deck-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block font-rajdhani font-semibold uppercase tracking-widest text-white/40 text-xs mb-1.5">Full Name *</label>
                        <input
                          value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                          placeholder="Your name"
                          data-testid="pitch-name-input"
                          className="w-full h-11 px-4 rounded-lg bg-white/5 border border-white/10 focus:border-bvx-cyan/50 focus:outline-none text-white text-sm font-inter-tight placeholder:text-white/25 transition-colors duration-200"
                        />
                      </div>
                      <div>
                        <label className="block font-rajdhani font-semibold uppercase tracking-widest text-white/40 text-xs mb-1.5">Email *</label>
                        <input
                          type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                          placeholder="you@fund.com"
                          data-testid="pitch-email-input"
                          className="w-full h-11 px-4 rounded-lg bg-white/5 border border-white/10 focus:border-bvx-cyan/50 focus:outline-none text-white text-sm font-inter-tight placeholder:text-white/25 transition-colors duration-200"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block font-rajdhani font-semibold uppercase tracking-widest text-white/40 text-xs mb-1.5">Organization / Fund</label>
                      <input
                        value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })}
                        placeholder="Firm or organization name"
                        data-testid="pitch-org-input"
                        className="w-full h-11 px-4 rounded-lg bg-white/5 border border-white/10 focus:border-bvx-cyan/50 focus:outline-none text-white text-sm font-inter-tight placeholder:text-white/25 transition-colors duration-200"
                      />
                    </div>
                    <div>
                      <label className="block font-rajdhani font-semibold uppercase tracking-widest text-white/40 text-xs mb-1.5">Investment Range</label>
                      <select
                        value={form.investment_range} onChange={(e) => setForm({ ...form, investment_range: e.target.value })}
                        data-testid="pitch-range-select"
                        className="w-full h-11 px-4 rounded-lg bg-white/5 border border-white/10 focus:border-bvx-cyan/50 focus:outline-none text-white text-sm font-inter-tight transition-colors duration-200"
                      >
                        <option value="" className="bg-bvx-card">Select range (optional)</option>
                        <option value="under-50k" className="bg-bvx-card">Under $50K</option>
                        <option value="50k-250k" className="bg-bvx-card">$50K – $250K</option>
                        <option value="250k-1m" className="bg-bvx-card">$250K – $1M</option>
                        <option value="1m-plus" className="bg-bvx-card">$1M+</option>
                        <option value="strategic" className="bg-bvx-card">Strategic Partnership</option>
                      </select>
                    </div>
                    <div>
                      <label className="block font-rajdhani font-semibold uppercase tracking-widest text-white/40 text-xs mb-1.5">Message (Optional)</label>
                      <textarea
                        value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                        placeholder="Tell us about your investment focus or any specific questions..."
                        rows={3}
                        data-testid="pitch-message-input"
                        className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 focus:border-bvx-cyan/50 focus:outline-none text-white text-sm font-inter-tight placeholder:text-white/25 transition-colors duration-200 resize-none"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="pitch-submit-btn"
                    >
                      <Send size={14} />
                      {loading ? 'Sending...' : 'Request Pitch Deck'}
                    </button>
                  </form>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

    </main>
  );
}
